import { Link } from "gatsby";

import { PERSONAL_EMAIL_DOMAINS_BLACKLIST } from "./common";

// Phone number formatting and validation
const invalidPhoneCharactersRegex = /[^0-9()\- ]/g;
const phoneNumberToFormatRegex =
    /^\(?([0-9]{3})?\)?[ ]?([0-9]{3})?-?([0-9]{4})?$/;

const formatPhoneNumber = (
    match,
    firstThreeDigits,
    middleThreeDigits,
    lastFourDigits
) => {
    let formattedNumber = "";
    const exceptions = /^\([0-9]{3}\)?$/;

    // Treat exceptions so that the user is not prevented from entering or deleting certain characters
    if (exceptions.test(match) || (lastFourDigits && !middleThreeDigits)) {
        return match;
    }

    if (firstThreeDigits) {
        formattedNumber += `(${firstThreeDigits.replace(/[()]/g, "")})`;
    }
    if (middleThreeDigits) {
        formattedNumber += " " + middleThreeDigits;
    }
    if (lastFourDigits) {
        formattedNumber += "-" + lastFourDigits;
    }

    return formattedNumber || match;
};

export const getFormattedPhoneNumber = number => {
    if (invalidPhoneCharactersRegex.test(number)) {
        return number.replace(invalidPhoneCharactersRegex, "");
    }
    const formattedNumber = number.replace(
        phoneNumberToFormatRegex,
        formatPhoneNumber
    );

    return formattedNumber.slice(0, 14);
};

export const isValidPhoneNumber = phoneNumber =>
    phoneNumberToFormatRegex.test(phoneNumber) && phoneNumber.length === 14;

const emailRegex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const isValidEmail = email => email && emailRegex.test(email.trim());

const nameRegex = /^[a-zA-Z]{1}[-',. a-zA-Z]*$/;

export const isValidName = name => name && nameRegex.test(name.trim());

const zipRegex = /^\d{5}$/;

export const isValidZipCode = zipCode =>
    zipCode && zipRegex.test(zipCode.trim());

export const hydrateBlogPosts = result => {
    if (result?.edges) {
        return result.edges.map(({ node }) => node);
    }
    return result;
};

export const hydrateFeaturedBlogPosts = result => {
    if (result?.length) {
        return result.map(node => node);
    }
    return result;
};

// Aliases
export const hydratePressReleases = hydrateBlogPosts;

export const isValidHexColor = hexColor => {
    return /^#([a-f0-9]{3}){1,2}$/i.test(hexColor);
};

const youtubeRegex =
    /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;

export const isYoutubeUrl = url => {
    return youtubeRegex.test(url);
};

export const getYoutubeId = url => {
    const match = url.match(youtubeRegex);

    if (match && match[7].length === 11) {
        return match[7];
    }

    return null;
};

export const getYouTubeThumbnail = (url, format) => {
    const id = getYoutubeId(url);

    if (format === "webp") {
        return `https://i.ytimg.com/vi_webp/${id}/maxresdefault.webp`;
    }

    return `https://i3.ytimg.com/vi/${id}/maxresdefault.jpg`;
};

export const getFormLead = (title, lead) => {
    // This function generates an object representing a lead copy that can be fed to a RichTextBlock component,
    // just like a block from Contentful would be.
    return {
        raw: `{"nodeType":"document","data":{},"content":[{"nodeType":"heading-3","content":[{"nodeType":"text","value":"${title}","marks":[],"data":{}}],"data":{}},{"nodeType":"paragraph","content":[{"nodeType":"text","value":"${lead}","marks":[],"data":{}}],"data":{}}]}`,
    };
};

export const getNavItemProps = ({
    id,
    htmlId,
    externalLink,
    page,
    openInNewTab,
}) => {
    const props = {
        id: htmlId,
        key: id,
    };

    if (externalLink) {
        props.as = "a";
        props.href = externalLink;

        if (openInNewTab) {
            props.target = "_blank";
            props.rel = "noreferrer";
        }
    } else if (page) {
        props.as = Link;
        //Ensure ending with /
        props.to = page.path.replace(/\/?$/, "/");
    }

    return props;
};

const extractEmailDomain = email => {
    const domain = email.split("@").pop();
    return domain;
};

export const isEmailDomainAllow = email => {
    if (!email) return false;

    const domain = extractEmailDomain(email);

    return !PERSONAL_EMAIL_DOMAINS_BLACKLIST?.includes(domain);
};

export const getCtaProps = (link, pdf) => {
    if (!pdf?.url && !link) return;

    if (link?.startsWith("/")) {
        return {
            as: Link,
            to: link,
        };
    }

    return {
        as: "a",
        href: pdf?.url || link,
        download: !!pdf,
        target: "_blank",
        rel: "noreferrer",
    };
};

const websiteRegex =
    /^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[a-zA-Z0-9-._~:?#[\]@!$&'()*+,;=]*)*$/;

export const isWebsiteValid = websiteUrl =>
    websiteUrl && websiteRegex.test(websiteUrl.trim());

const httpUrlRegex = /^(https?:\/\/)/;

export const isValidHttpUrl = url => url && httpUrlRegex.test(url.trim());
